import React, { useState } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
    Shield,
    Users,
    Shuffle,
    Zap,
    Settings,
    Database,
    LogOut,
    ChevronRight,
    ChevronLeft,
    ArrowLeftFromLine,
    LayoutDashboard,
    Link,
    Home
} from 'lucide-react';

const getAdminNavItems = (t) => [
    {
        id: 'users',
        label: t('teamMembers') || 'Team Members',
        description: t('manageUsersRoles') || 'Manage users & roles',
        icon: Users,
        section: 'users'
    },
    {
        id: 'assignment',
        label: t('assignmentRules') || 'Assignment Rules',
        description: t('leadRoutingLogic') || 'Lead routing logic',
        icon: Shuffle,
        section: 'assignment'
    },
    {
        id: 'automation',
        label: t('automation') || 'Automation',
        description: t('perSourceTriggers') || 'Per-source triggers',
        icon: Zap,
        section: 'automation'
    },
    {
        id: 'settings',
        label: t('appSettings') || 'App Settings',
        description: t('eventTypesConfig') || 'Event types & config',
        icon: Settings,
        section: 'settings'
    }
];

export default function AdminSidebar({ activeSection, onSectionChange, collapsed, onToggleCollapse, isMobile = false }) {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const location = useLocation();

    const handleExitToApp = () => {
        navigate('/');
    };

    const adminNavItems = getAdminNavItems(t);

    // ── Mobile layout (matches RoleSidebar mobile grid) ──────────────────────
    if (isMobile) {
        return (
            <div className="p-2">
                {/* Navigation Grid */}
                <div className="grid grid-cols-2 gap-2">
                    {adminNavItems.map((item) => {
                        const isActive = activeSection === item.section && location.pathname === '/admin';
                        return (
                            <button
                                key={item.id}
                                onClick={() => {
                                    if (location.pathname !== '/admin') {
                                        navigate('/admin', { state: { activeSection: item.section } });
                                    } else {
                                        if (onSectionChange) onSectionChange(item.section);
                                    }
                                }}
                                className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all ${isActive
                                    ? 'bg-purple-100 text-purple-700 font-semibold'
                                    : 'bg-gray-50 text-gray-600 active:bg-gray-100'
                                    }`}
                            >
                                <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-purple-600' : 'text-gray-400'}`} />
                                <span className="text-sm font-medium truncate">{item.label}</span>
                            </button>
                        );
                    })}

                    {/* Integrations */}
                    <NavLink
                        to="/integrations"
                        className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-3 rounded-xl text-left transition-all ${isActive || location.pathname === '/integrations'
                                ? 'bg-cyan-100 text-cyan-700 font-semibold'
                                : 'bg-gray-50 text-gray-600 active:bg-gray-100'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <Link className={`w-5 h-5 flex-shrink-0 ${isActive || location.pathname === '/integrations' ? 'text-cyan-600' : 'text-gray-400'}`} />
                                <span className="text-sm font-medium truncate">{t('integrations') || 'Integrations'}</span>
                            </>
                        )}
                    </NavLink>
                </div>

                {/* Divider */}
                <div className="my-3 border-t border-gray-200"></div>

                {/* Exit to App */}
                <button
                    onClick={handleExitToApp}
                    className="w-full flex items-center gap-2 px-3 py-3 rounded-xl text-left bg-blue-50 text-blue-700 border border-blue-100 active:bg-blue-100"
                >
                    <ArrowLeftFromLine className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-sm font-semibold">{t('exitToApp') || 'Exit to App'}</span>
                </button>
            </div>
        );
    }

    // ── Desktop layout (collapsible sidebar) ─────────────────────────────────
    return (
        <aside
            className="h-full bg-white flex flex-col relative"
            style={{ width: '100%' }}
        >
            {/* Toggle Button */}
            <div className="absolute top-6 -right-4 z-50">
                <button
                    onClick={onToggleCollapse}
                    className="group flex items-center justify-center w-8 h-8 !min-h-[32px] rounded-full bg-white border border-gray-200 text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 shadow-sm"
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {collapsed ? (
                        <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
                    ) : (
                        <ChevronLeft className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
                    )}
                </button>
            </div>

            {/* Navigation */}
            <nav className={`flex-1 ${collapsed ? 'px-2 py-3' : 'p-4'} space-y-1 overflow-y-auto overflow-x-hidden`}>
                {adminNavItems.map((item) => {
                    const isActive = activeSection === item.section && location.pathname === '/admin';
                    return (
                        <button
                            key={item.id}
                            onClick={() => {
                                if (location.pathname !== '/admin') {
                                    navigate('/admin', { state: { activeSection: item.section } });
                                } else {
                                    if (onSectionChange) onSectionChange(item.section);
                                }
                            }}
                            className={`
                group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
                ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
                ${isActive
                                    ? 'bg-purple-50 text-purple-700 font-semibold shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }
              `}
                        >
                            <item.icon
                                className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${isActive ? 'text-purple-600' : 'text-gray-400 group-hover:text-gray-600'
                                    }`}
                            />
                            {!collapsed && (
                                <div className="min-w-0 overflow-hidden">
                                    <p className="text-sm font-medium truncate whitespace-nowrap">{item.label}</p>
                                    {!isActive && (
                                        <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{item.description}</p>
                                    )}
                                </div>
                            )}
                            {collapsed && (
                                <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
                  opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
                  pointer-events-none whitespace-nowrap z-50 shadow-lg">
                                    {item.label}
                                </div>
                            )}
                        </button>
                    );
                })}

                {/* Integrations */}
                <NavLink
                    to="/integrations"
                    className={({ isActive }) => `
                group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
                ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
                ${isActive || location.pathname === '/integrations'
                            ? 'bg-cyan-50 text-cyan-700 font-semibold shadow-sm'
                            : 'text-gray-600 hover:bg-cyan-50 hover:text-cyan-700'
                        }`}
                >
                    {({ isActive }) => {
                        const isCurrent = isActive || location.pathname === '/integrations';
                        return (
                            <>
                                <Link className={`w-5 h-5 flex-shrink-0 transition-colors duration-200 ${isCurrent ? 'text-cyan-600' : 'text-cyan-500'}`} />
                                {!collapsed && (
                                    <div className="min-w-0 overflow-hidden">
                                        <p className="text-sm font-medium truncate whitespace-nowrap">{t('integrations') || 'Integrations'}</p>
                                        {!isCurrent && <p className="text-[10px] text-gray-400 truncate whitespace-nowrap">{t('whatsappMetaAi') || 'WhatsApp, Meta, AI'}</p>}
                                    </div>
                                )}
                                {collapsed && (
                                    <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
                        opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
                        pointer-events-none whitespace-nowrap z-50 shadow-lg">
                                        {t('integrations') || 'Integrations'}
                                    </div>
                                )}
                            </>
                        );
                    }}
                </NavLink>

            </nav>

            {/* Exit to App Button */}
            <div className={`${collapsed ? 'p-2' : 'p-4'} border-t border-gray-100`}>
                <button
                    onClick={handleExitToApp}
                    className={`
            group relative w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3
            ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} rounded-xl text-left transition-all duration-200
            bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 hover:from-blue-100 hover:to-indigo-100
            border border-blue-200 hover:border-blue-300 shadow-sm
          `}
                    title={t('exitToApp') || 'Exit to App'}
                >
                    <ArrowLeftFromLine className="w-5 h-5 flex-shrink-0 text-blue-600 transition-transform duration-200 group-hover:-translate-x-0.5" />
                    {!collapsed && (
                        <div className="min-w-0 overflow-hidden">
                            <p className="text-sm font-semibold truncate whitespace-nowrap text-blue-700">{t('exitToApp') || 'Exit to App'}</p>
                            <p className="text-[10px] text-blue-400 truncate whitespace-nowrap">{t('backToMainDashboard') || 'Back to main dashboard'}</p>
                        </div>
                    )}
                    {collapsed && (
                        <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg 
              opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 
              pointer-events-none whitespace-nowrap z-50 shadow-lg">
                            {t('exitToApp') || 'Exit to App'}
                        </div>
                    )}
                </button>
            </div>
        </aside>
    );
}

